import React, { useCallback, useState } from "react";
import {
  Page,
  Layout,
  Card,
  Banner,
  Text,
  BlockStack,
  InlineStack,
  Button,
  SkeletonBodyText,
  EmptyState,
  ResourceList,
  ResourceItem,
  Thumbnail,
  Badge,
  List,
  Link,
} from "@shopify/polaris";
import { ImageIcon } from "@shopify/polaris-icons";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_CHANNEL_PUBLICATION_SUMMARY,
  GET_CHANNEL_PUBLISHED_PRODUCTS,
} from "../../graphql/queries";
import {
  PUBLISH_PRODUCTS_TO_CHANNEL,
  UNPUBLISH_PRODUCTS_FROM_CHANNEL,
} from "../../graphql/mutations";
import { useAppBridge } from "../../Components/Shopify/ShopifyAppBridgeProvider";
import ChannelHelpFooter, { MARKETPLACE_URL } from "../../Components/Channel/ChannelHelpFooter";

type ChannelProduct = {
  id: string;
  title: string;
  handle?: string | null;
  status: string;
  totalInventory?: number | null;
  tracksInventory?: boolean | null;
  imageUrl?: string | null;
  issues: string[];
};

type ChannelProductPage = {
  products: ChannelProduct[];
  hasNextPage: boolean;
  endCursor?: string | null;
};

type PublicationSummary = {
  connected: boolean;
  channelId?: string | null;
  channelHandle?: string | null;
  accountName?: string | null;
  publicationId?: string | null;
  publishedCount: number;
  bulkEditorUrl?: string | null;
  marketplaceUrl: string;
  error?: string | null;
};

type PublishResult = {
  succeeded: string[];
  failed: { productId: string; message: string }[];
};

const PAGE_SIZE = 25;
const REFRESH_RETRIES = 3;
const REFRESH_RETRY_DELAY_MS = 1500;

function describeResult(verb: string, result?: PublishResult | null): string {
  if (!result) return `${verb} failed. Please try again.`;
  const ok = result.succeeded.length;
  const bad = result.failed.length;
  const parts: string[] = [];
  if (ok) parts.push(`${ok} product${ok === 1 ? "" : "s"} ${verb.toLowerCase()}`);
  if (bad) parts.push(`${bad} failed: ${result.failed.map((f) => f.message).join("; ")}`);
  return parts.join(". ") || `Nothing was ${verb.toLowerCase()}.`;
}

const Publishing: React.FC = () => {
  // Wait for App Bridge to provide window.shopify.idToken before firing the
  // queries — otherwise the Apollo authLink can't attach the Authorization
  // header and the backend rejects with "Missing authorization header".
  const { isReady, isEmbedded } = useAppBridge();
  const skip = isEmbedded && !isReady;

  const summaryQuery = useQuery<{ channelPublicationSummary: PublicationSummary }>(
    GET_CHANNEL_PUBLICATION_SUMMARY,
    { fetchPolicy: "cache-and-network", skip }
  );
  // The product list waits for the summary: on a fresh install the summary
  // call is what clears Shopify's auto-seeded catalog from the publication,
  // and reading the list in parallel would briefly show every product as
  // "Listed" until the next refresh.
  const summaryReady = Boolean(summaryQuery.data?.channelPublicationSummary);
  const productsQuery = useQuery<{ channelPublishedProducts: ChannelProductPage }>(
    GET_CHANNEL_PUBLISHED_PRODUCTS,
    {
      variables: { first: PAGE_SIZE, after: null },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip: skip || !summaryReady,
    }
  );
  const [publishProducts, { loading: publishing }] = useMutation<{
    publishProductsToChannel: PublishResult;
  }>(PUBLISH_PRODUCTS_TO_CHANNEL);
  const [unpublishProducts, { loading: unpublishing }] = useMutation<{
    unpublishProductsFromChannel: PublishResult;
  }>(UNPUBLISH_PRODUCTS_FROM_CHANNEL);

  const [notice, setNotice] = useState<{ tone: "success" | "warning" | "critical"; text: string } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const summary = summaryQuery.data?.channelPublicationSummary ?? null;
  const page = productsQuery.data?.channelPublishedProducts ?? null;
  const summaryLoading = skip || (summaryQuery.loading && !summary);
  const productsLoading = skip || !summaryReady || (productsQuery.loading && !page);
  const busy = publishing || unpublishing;

  const refresh = useCallback(async () => {
    const [summaryResult] = await Promise.all([
      summaryQuery.refetch(),
      productsQuery.refetch({ first: PAGE_SIZE, after: null }),
    ]);
    return summaryResult.data?.channelPublicationSummary?.publishedCount ?? null;
  }, [summaryQuery, productsQuery]);

  // Shopify applies publish/unpublish a moment after the mutation returns, so
  // a single refetch can still read the old count. Re-poll briefly until the
  // count moves off its pre-mutation value (or we give up).
  const refreshAfterChange = useCallback(
    async (previousCount: number | null) => {
      let count = await refresh();
      for (let attempt = 0; attempt < REFRESH_RETRIES && count === previousCount; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, REFRESH_RETRY_DELAY_MS));
        count = await refresh();
      }
    },
    [refresh]
  );

  // App Bridge v4 resource picker: merchants pick products straight from
  // their catalog; the backend publishes them to the ShareStock publication.
  const addProducts = useCallback(async () => {
    setNotice(null);
    const picker = window.shopify?.resourcePicker;
    if (!picker) {
      setNotice({
        tone: "critical",
        text: "The product picker is only available inside Shopify admin. Open ShareStock from your Apps page.",
      });
      return;
    }
    let selection: { id: string }[] | undefined;
    try {
      selection = await picker({
        type: "product",
        multiple: true,
        action: "select",
        filter: { variants: false, archived: false },
      });
    } catch {
      return;
    }
    if (!selection || selection.length === 0) return;
    const previousCount = summary?.publishedCount ?? null;
    try {
      const { data } = await publishProducts({
        variables: { productIds: selection.map((p) => p.id) },
      });
      const result = data?.publishProductsToChannel;
      setNotice({
        tone: result && result.failed.length ? "warning" : "success",
        text: describeResult("Published", result),
      });
      await refreshAfterChange(result?.succeeded.length ? previousCount : null);
    } catch (error: any) {
      setNotice({ tone: "critical", text: error?.message || "Publishing failed. Please try again." });
    }
  }, [publishProducts, refreshAfterChange, summary]);

  const removeProduct = useCallback(
    async (productId: string) => {
      setNotice(null);
      const previousCount = summary?.publishedCount ?? null;
      try {
        const { data } = await unpublishProducts({ variables: { productIds: [productId] } });
        const result = data?.unpublishProductsFromChannel;
        setNotice({
          tone: result && result.failed.length ? "warning" : "success",
          text: describeResult("Removed", result),
        });
        await refreshAfterChange(result?.succeeded.length ? previousCount : null);
      } catch (error: any) {
        setNotice({ tone: "critical", text: error?.message || "Could not remove the product." });
      }
    },
    [unpublishProducts, refreshAfterChange, summary]
  );

  const loadMore = useCallback(async () => {
    if (!page?.hasNextPage || !page.endCursor) return;
    setLoadingMore(true);
    try {
      await productsQuery.fetchMore({
        variables: { first: PAGE_SIZE, after: page.endCursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          return {
            channelPublishedProducts: {
              ...fetchMoreResult.channelPublishedProducts,
              products: [
                ...prev.channelPublishedProducts.products,
                ...fetchMoreResult.channelPublishedProducts.products,
              ],
            },
          };
        },
      });
    } finally {
      setLoadingMore(false);
    }
  }, [page, productsQuery]);

  const queryError = summaryQuery.error?.message || productsQuery.error?.message || null;
  const channelProblem = summary && !summary.connected ? summary.error : null;

  return (
    <Page
      title="Publishing"
      subtitle="Choose which products appear on the ShareStock marketplace"
      backAction={{ content: "Home", url: "/" }}
      primaryAction={{
        content: "Add products",
        onAction: addProducts,
        loading: publishing,
        disabled: busy || !summary?.connected,
      }}
    >
      <Layout>
        {queryError && (
          <Layout.Section>
            <Banner title="Couldn't load publishing data" tone="critical">
              <Text as="p" variant="bodyMd">
                {queryError}
              </Text>
            </Banner>
          </Layout.Section>
        )}

        {channelProblem && (
          <Layout.Section>
            <Banner title="ShareStock sales channel isn't connected yet" tone="warning">
              <Text as="p" variant="bodyMd">
                {channelProblem}
              </Text>
            </Banner>
          </Layout.Section>
        )}

        {notice && (
          <Layout.Section>
            <Banner tone={notice.tone} onDismiss={() => setNotice(null)}>
              <Text as="p" variant="bodyMd">
                {notice.text}
              </Text>
            </Banner>
          </Layout.Section>
        )}

        <Layout.AnnotatedSection
          title="Published products"
          description={
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd" tone="subdued">
                Products you publish to the ShareStock sales channel are listed on the
                marketplace with their live Shopify price and inventory.
              </Text>
              <Text as="p" variant="bodyMd" tone="subdued">
                You can also publish from any product page in Shopify under
                "Sales channels and apps", or in bulk with the bulk editor.
              </Text>
            </BlockStack>
          }
        >
          <Card>
            <BlockStack gap="400">
              {summaryLoading ? (
                <SkeletonBodyText lines={3} />
              ) : (
                <InlineStack align="space-between" blockAlign="center" wrap>
                  <BlockStack gap="100">
                    <Text as="h3" variant="headingLg">
                      {summary?.publishedCount ?? 0}
                    </Text>
                    <Text as="p" variant="bodyMd" tone="subdued">
                      product{summary?.publishedCount === 1 ? "" : "s"} published to ShareStock
                      {summary?.accountName ? ` (${summary.accountName})` : ""}
                    </Text>
                  </BlockStack>
                  <InlineStack gap="200">
                    <Button
                      variant="primary"
                      onClick={addProducts}
                      loading={publishing}
                      disabled={busy || !summary?.connected}
                    >
                      Add products
                    </Button>
                    {summary?.bulkEditorUrl && (
                      <Button url={summary.bulkEditorUrl} target="_top">
                        Open bulk editor
                      </Button>
                    )}
                  </InlineStack>
                </InlineStack>
              )}
            </BlockStack>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title="Product status"
          description="Products that need attention are flagged here and on the product page in Shopify. Fix the issue and the listing updates automatically."
        >
          <Card padding="0">
            {productsLoading ? (
              <div style={{ padding: "var(--p-space-400)" }}>
                <SkeletonBodyText lines={6} />
              </div>
            ) : !page || page.products.length === 0 ? (
              <EmptyState
                heading="No products published yet"
                image=""
                action={{
                  content: "Add products",
                  onAction: addProducts,
                  disabled: busy || !summary?.connected,
                }}
              >
                <Text as="p" variant="bodyMd">
                  Pick the products you want buyers to see on ShareStock. Remove a product
                  here or unpublish it from its product page to take it off the marketplace.
                </Text>
              </EmptyState>
            ) : (
              <ResourceList
                resourceName={{ singular: "product", plural: "products" }}
                items={page.products}
                renderItem={(product) => {
                  const needsAttention = product.issues.length > 0;
                  return (
                    <ResourceItem
                      id={product.id}
                      onClick={() => undefined}
                      accessibilityLabel={`View details for ${product.title}`}
                      media={
                        <Thumbnail
                          source={product.imageUrl || ImageIcon}
                          alt={product.title}
                          size="small"
                        />
                      }
                      shortcutActions={[
                        {
                          content: "Remove",
                          onAction: () => removeProduct(product.id),
                          disabled: busy,
                        },
                      ]}
                    >
                      <BlockStack gap="100">
                        <InlineStack gap="200" blockAlign="center" wrap>
                          <Text as="span" variant="bodyMd" fontWeight="semibold">
                            {product.title}
                          </Text>
                          <Badge tone={needsAttention ? "attention" : "success"}>
                            {needsAttention ? "Needs attention" : "Listed"}
                          </Badge>
                          {product.status !== "ACTIVE" && (
                            <Badge tone="info">{product.status.toLowerCase()}</Badge>
                          )}
                        </InlineStack>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {product.tracksInventory === false
                            ? "Inventory not tracked"
                            : `${product.totalInventory ?? 0} in stock`}
                        </Text>
                        {needsAttention && (
                          <List type="bullet">
                            {product.issues.map((issue) => (
                              <List.Item key={issue}>{issue}</List.Item>
                            ))}
                          </List>
                        )}
                      </BlockStack>
                    </ResourceItem>
                  );
                }}
              />
            )}
            {page?.hasNextPage && (
              <div style={{ padding: "var(--p-space-400)" }}>
                <InlineStack align="center">
                  <Button onClick={loadMore} loading={loadingMore}>
                    Load more
                  </Button>
                </InlineStack>
              </div>
            )}
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title="Where products appear"
          description="Buyers discover your products on the ShareStock marketplace and check out through your store's Shopify Checkout."
        >
          <Card>
            <BlockStack gap="300">
              <Text as="p" variant="bodyMd">
                Published products are listed at{" "}
                <Link url={summary?.marketplaceUrl || MARKETPLACE_URL} target="_blank">
                  mysharestock.org/browse
                </Link>
                . Orders placed there appear in your Shopify Orders list attributed to the
                ShareStock sales channel.
              </Text>
              <InlineStack gap="200">
                <Button url={summary?.marketplaceUrl || MARKETPLACE_URL} target="_blank">
                  View marketplace
                </Button>
                <Button url="shopify://admin/orders" target="_top">
                  Open orders
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.Section>
          <ChannelHelpFooter />
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default Publishing;
